import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import type { ReviewerRecord, RegisterReviewerInput } from '@/types/api';

interface EditReviewerDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly reviewer: ReviewerRecord;
}

type ReviewerRole = RegisterReviewerInput['role'];

const ROLE_OPTIONS: { value: ReviewerRole; label: string }[] = [
  { value: 'cco', label: 'Chief Compliance Officer' },
  { value: 'compliance_rep', label: 'Compliance Representative' },
  { value: 'department_rep', label: 'Department Representative' },
];

export function EditReviewerDialog({ open, onOpenChange, reviewer }: EditReviewerDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(reviewer.name);
  const [email, setEmail] = useState(reviewer.email);
  const [role, setRole] = useState<ReviewerRole>(reviewer.role);
  const [selectedDepts, setSelectedDepts] = useState<string[]>([...reviewer.departments]);

  useEffect(() => {
    setName(reviewer.name);
    setEmail(reviewer.email);
    setRole(reviewer.role);
    setSelectedDepts([...reviewer.departments]);
  }, [reviewer]);

  const { data: tenant } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => api.admin.tenant.get(),
    staleTime: 300_000,
  });

  const availableDepts = tenant?.activeDepartments ?? [];

  const updateMutation = useMutation({
    mutationFn: (input: Partial<Omit<RegisterReviewerInput, 'password'>>) =>
      api.admin.reviewers.update(reviewer.reviewerId, input),
    onSuccess: () => {
      toast.success('Reviewer updated.');
      queryClient.invalidateQueries({ queryKey: ['reviewers'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to update reviewer.');
    },
  });

  const toggleDept = (dept: string) => {
    setSelectedDepts((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept],
    );
  };

  const hasChanges =
    name !== reviewer.name ||
    email !== reviewer.email ||
    role !== reviewer.role ||
    JSON.stringify([...selectedDepts].sort()) !== JSON.stringify([...reviewer.departments].sort());

  const isValid =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    selectedDepts.length > 0 &&
    hasChanges;

  const handleSubmit = () => {
    updateMutation.mutate({
      name: name.trim(),
      email: email.trim(),
      role,
      departments: selectedDepts,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Reviewer</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-name">Full Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-role">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as ReviewerRole)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Departments</Label>
            <div className="grid grid-cols-2 gap-2">
              {availableDepts.map((dept) => (
                <label
                  key={dept}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm transition-colors hover:bg-muted/30"
                >
                  <Checkbox
                    checked={selectedDepts.includes(dept)}
                    onCheckedChange={() => toggleDept(dept)}
                  />
                  <span className="capitalize text-foreground">
                    {dept.replace(/_/g, ' ')}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-teal text-white hover:bg-teal/90"
            disabled={!isValid || updateMutation.isPending}
            onClick={handleSubmit}
          >
            {updateMutation.isPending && (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
